import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";

import { getAuthorizationToken } from "@/auth/tokenStore";

import { getCaseId, getClientId } from "../../Utils/helper";
import { api_without_cancellation } from "../../api/api";
import { getFirmManagementUrl } from "../../Routes/firmManagement.mjs";

import WPBase from "./WPBase";

function WPLetterTemplate({
  docId,
  updatedDocId,
  dynamicTemplateId,
  editDocId,
  initialExpiresAt,
  dispatch,
  state,
  setTotalPages,
  setPageNumber,
  container,
}) {
  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
  const [docName, setDocName] = useState("Document");
  const [pageId, setPageId] = useState(null);
  const [dropdownId, setDropdownId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(
    initialExpiresAt || new Date(Date.now() + 3 * 60 * 1000).toISOString()
  );
  const [secondsRemaining, setSecondsRemaining] = useState(180);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [saveCountdown, setSaveCountdown] = useState(5);
  const [hasPendingUpdate, setHasPendingUpdate] = useState(false);

  const activeTemplateIdRef = useRef(dynamicTemplateId);
  const selectedCategoryRef = useRef(null);
  const revisionRef = useRef(0);
  const queueRef = useRef(Promise.resolve());
  const pendingCountRef = useRef(0);
  const dirtyGenerationRef = useRef(0);
  const storedGenerationRef = useRef(0);
  const releasedRef = useRef(false);
  const closingRef = useRef(false);
  const hasObservedActiveLeaseRef = useRef(false);
  const hasAttemptedInitialRenewalRef = useRef(false);
  const isRenewingExpiredLeaseRef = useRef(false);

  const updateExpiry = useCallback((payload) => {
    const nextExpiry = payload?.expires_at || payload?.session?.expires_at;
    if (nextExpiry) setExpiresAt(nextExpiry);
    const workingRevision = payload?.working_revision ?? payload?.session?.working_revision;
    if (Number.isFinite(Number(workingRevision))) {
      revisionRef.current = Math.max(revisionRef.current, Number(workingRevision));
    }
  }, []);

  const updateSessionTarget = useCallback((payload) => {
    const templateId = payload?.template_id;
    const targetDocId = payload?.doc_id;
    if (!templateId) return;
    activeTemplateIdRef.current = templateId;
    const params = new URLSearchParams(window.location.search);
    params.set("dynamic_template_id", templateId);
    if (targetDocId) params.set("docId", targetDocId);
    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
  }, []);

  const enqueue = useCallback((operation) => {
    pendingCountRef.current += 1;
    setHasPendingUpdate(true);
    const result = queueRef.current.then(operation, operation);
    queueRef.current = result.catch(() => undefined);
    const finish = () => {
      pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
      setHasPendingUpdate(pendingCountRef.current > 0);
    };
    result.then(finish, finish);
    return result;
  }, []);

  const postSessionUpdate = useCallback(
    (url, { file, category = undefined, fileName = undefined, final = false } = {}) =>
      enqueue(async () => {
        const formData = new FormData();
        formData.append("edit_doc_id", editDocId);
        const hasChange = file || category !== undefined || fileName !== undefined;
        if (hasChange) {
          revisionRef.current += 1;
          formData.append("revision", revisionRef.current);
        }
        if (file) formData.append("file", file);
        if (category !== undefined) {
          formData.append("last_category", category === null ? "" : category);
        }
        if (fileName !== undefined) formData.append("file_name", fileName || "");

        const response = await api_without_cancellation.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        updateExpiry(response.data);
        if (final) updateSessionTarget(response.data);
        return response.data;
      }),
    [editDocId, enqueue, updateExpiry, updateSessionTarget]
  );

  const storeContent = useCallback(
    async (file, generation) => {
      const data = await postSessionUpdate("/api/update-dlt-edit-doc/", { file });
      storedGenerationRef.current = Math.max(storedGenerationRef.current, generation);
      return data;
    },
    [postSessionUpdate]
  );

  const markContentDirty = useCallback((generation) => {
    dirtyGenerationRef.current = Math.max(dirtyGenerationRef.current, generation);
  }, []);

  const persistCategory = useCallback(
    (category) => {
      selectedCategoryRef.current = category;
      return postSessionUpdate("/api/update-dlt-edit-doc/", { category });
    },
    [postSessionUpdate]
  );

  const persistRename = useCallback(
    async (fileName) => {
      await postSessionUpdate("/api/update-dlt-edit-doc/", { fileName });
      setDocName(fileName);
    },
    [postSessionUpdate]
  );

  const serializeDocument = useCallback(async () => {
    if (!container.current) throw new Error("Editor not ready");
    const content = await container.current.save();
    if (!content || content.byteLength === 0) throw new Error("Document content is empty");
    return new File([content], `${docName || "document"}.docx`, {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }, [container, docName]);

  const checkpointDocument = useCallback(async () => {
    try {
      const generation = dirtyGenerationRef.current;
      const file = await serializeDocument();
      const result = await postSessionUpdate("/api/save-dlt-edit-doc/", {
        file,
        category: selectedCategoryRef.current,
        fileName: docName,
        final: true,
      });
      storedGenerationRef.current = Math.max(storedGenerationRef.current, generation);
      updateSessionTarget(result);
      setSaveCountdown(5);
      setShowSaveConfirmation(true);
    } catch (error) {
      console.error("Error saving DLT:", error);
    }
  }, [docName, postSessionUpdate, serializeDocument, updateSessionTarget]);

  const closeDocument = useCallback(
    async ({ flush = true } = {}) => {
      if (closingRef.current || releasedRef.current) return;
      closingRef.current = true;
      try {
        let file;
        if (flush) file = await serializeDocument();
        const result = await postSessionUpdate("/api/close-dlt-edit-doc/", {
          file,
          category: flush ? selectedCategoryRef.current : undefined,
          fileName: flush ? docName : undefined,
          final: true,
        });
        releasedRef.current = true;
        updateSessionTarget(result);
        window.location.href = getFirmManagementUrl(getClientId(), getCaseId(), "templates");
      } catch (error) {
        closingRef.current = false;
        console.error("Error closing DLT:", error);
        if (error?.response?.status === 404) {
          releasedRef.current = true;
          window.location.href = getFirmManagementUrl(getClientId(), getCaseId(), "templates");
        }
      }
    },
    [docName, postSessionUpdate, serializeDocument, updateSessionTarget]
  );

  const renewSession = useCallback(async () => {
    try {
      const result = await postSessionUpdate("/api/update-dlt-edit-doc/");
      const renewedExpiry = result?.expires_at || result?.session?.expires_at;
      if (!renewedExpiry) {
        setExpiresAt(new Date(Date.now() + 3 * 60 * 1000).toISOString());
      }
      setShowExpiryWarning(false);
    } catch (error) {
      console.error("Error renewing DLT session:", error);
      await closeDocument({ flush: true });
    }
  }, [closeDocument, postSessionUpdate]);

  const discardSession = useCallback(
    () =>
      enqueue(async () => {
        const formData = new FormData();
        formData.append("edit_doc_id", editDocId);
        formData.append("discard", "true");
        const response = await api_without_cancellation.post("/api/close-dlt-edit-doc/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        releasedRef.current = true;
        return response.data;
      }),
    [editDocId, enqueue]
  );

  const acquireCopiedTemplate = useCallback(async (template) => {
    const formData = new FormData();
    const sourceDocId = template?.for_template?.template?.id;
    formData.append("doc_id", sourceDocId);
    formData.append("template_id", template.id);
    formData.append("edit_mode", "dlt_template");
    formData.append("url", window.location.href);
    formData.append("name", template?.for_template?.template_name || "");
    const response = await api_without_cancellation.post("/api/create-edit-doc/", formData);
    const session = response.data;
    return (
      `/bp-wordprocessor/${getClientId()}/${getCaseId()}/?docId=${session.doc_id}` +
      `&updatedDocId=${session.updated_doc_id}&dynamic_template_id=${session.template_id}` +
      `&editDocId=${session.id}&expiresAt=${encodeURIComponent(session.expires_at)}` +
      "&type=LetterTemplate"
    );
  }, []);

  const copyTemplate = async () => {
    try {
      const file = await serializeDocument();
      const payload = {
        template_id: activeTemplateIdRef.current,
        temp_name: state.updatedFileName,
        dropdown: dropdownId,
        page: pageId,
        file,
      };
      const response = await api_without_cancellation.post(
        "/api/firmsetting-page/copy-dl-template/",
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      await discardSession();
      window.location.href = await acquireCopiedTemplate(response.data);
    } catch (error) {
      console.error("Error copying DLT:", error);
    }
  };

  const downloadDocument = async () => {
    try {
      const file = await serializeDocument();
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading DLT:", error);
    }
  };

  useEffect(() => {
    const updateCountdown = () => {
      const expiryTime = new Date(expiresAt).getTime();
      if (!Number.isFinite(expiryTime)) return;

      const remaining = Math.max(0, Math.ceil((expiryTime - Date.now()) / 1000));
      setSecondsRemaining(remaining);
      setShowExpiryWarning(remaining > 0 && remaining <= 15);

      if (remaining > 0) {
        hasObservedActiveLeaseRef.current = true;
        return;
      }

      if (remaining === 0 && !closingRef.current && !releasedRef.current) {
        // The expiry supplied in the URL can be stale by the time the editor
        // mounts. Loading the page is active use, so renew that lease instead
        // of immediately closing the newly opened document.
        if (!hasObservedActiveLeaseRef.current) {
          if (hasAttemptedInitialRenewalRef.current || isRenewingExpiredLeaseRef.current) {
            return;
          }
          hasAttemptedInitialRenewalRef.current = true;
          isRenewingExpiredLeaseRef.current = true;
          renewSession().finally(() => {
            isRenewingExpiredLeaseRef.current = false;
          });
          return;
        }

        closeDocument({ flush: true });
      }
    };

    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(interval);
  }, [closeDocument, expiresAt, renewSession]);

  useEffect(() => {
    if (!showSaveConfirmation) return undefined;
    if (saveCountdown <= 0) {
      closeDocument({ flush: false });
      return undefined;
    }
    const timer = window.setTimeout(() => setSaveCountdown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [closeDocument, saveCountdown, showSaveConfirmation]);

  useEffect(() => {
    const beforeUnload = (event) => {
      const hasUnstoredChange =
        dirtyGenerationRef.current > storedGenerationRef.current || pendingCountRef.current > 0;
      if (hasUnstoredChange) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    const pageHide = () => {
      const isFullyStored =
        dirtyGenerationRef.current <= storedGenerationRef.current &&
        pendingCountRef.current === 0 &&
        !releasedRef.current;
      if (!isFullyStored) return;
      const formData = new FormData();
      formData.append("edit_doc_id", editDocId);
      const authorization = getAuthorizationToken();
      fetch(`${origin}/api/close-dlt-edit-doc/`, {
        method: "POST",
        body: formData,
        keepalive: true,
        credentials: "include",
        headers: authorization ? { Authorization: authorization } : {},
      }).catch(() => undefined);
    };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("pagehide", pageHide);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("pagehide", pageHide);
    };
  }, [editDocId, origin]);

  const controls = (
    <div className="p-l-5">
      <input
        type="text"
        className="form-control col height-25 p-l-5 p-0"
        value={state.updatedFileName}
        onChange={(event) =>
          dispatch({ type: "SET_UPDATED_FILENAME", payload: event.target.value })
        }
      />
      <div className="d-flex justify-content-end m-t-5 m-b-5">
        <button
          type="button"
          className="btn btn-primary fw-bold height-25 d-flex align-items-center justify-content-center"
          onClick={copyTemplate}
        >
          Save Template Copy
        </button>
      </div>
    </div>
  );

  return (
    <>
      <WPBase
        docId={docId}
        updatedDocId={updatedDocId}
        dynamicTemplateId={dynamicTemplateId}
        setPageId={setPageId}
        setDropdownId={setDropdownId}
        setDocName={setDocName}
        state={state}
        dispatch={dispatch}
        container={container}
        controls={controls}
        type="LetterTemplate"
        setPageNumber={setPageNumber}
        setTotalPages={setTotalPages}
        saveDocument={checkpointDocument}
        closeDocument={() => closeDocument({ flush: true })}
        downloadDocument={downloadDocument}
        onDLTAutoSave={storeContent}
        onDLTContentDirty={markContentDirty}
        onDLTCategoryChange={persistCategory}
        onDLTInitialCategory={(category) => {
          selectedCategoryRef.current = category;
        }}
        onDLTRename={persistRename}
      />

      <Modal show={showSaveConfirmation} onHide={() => setShowSaveConfirmation(false)} centered>
        <Modal.Header>
          <Modal.Title>{state.tempName} saved successfully!</Modal.Title>
        </Modal.Header>
        <Modal.Footer className="mt-0 flex-column">
          <Button variant="secondary" onClick={() => setShowSaveConfirmation(false)}>
            Dismiss
          </Button>
          <div>Automatically closing in {saveCountdown} seconds...</div>
        </Modal.Footer>
      </Modal>

      <Modal show={showExpiryWarning} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Editing session expiring</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          This template will save and close in {secondsRemaining} seconds.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => closeDocument({ flush: true })}>
            Close Document
          </Button>
          <Button variant="primary" onClick={renewSession}>
            Continue Editing
          </Button>
        </Modal.Footer>
      </Modal>

      {hasPendingUpdate ? <span className="visually-hidden">Saving template</span> : null}
    </>
  );
}

export default WPLetterTemplate;
